import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { BadgeType } from '@humi-design-library/components/Badge/types';

import { Feedback } from '@models/performance/feedback.model';
import { Goal } from '@models/performance/goal.model';
import { ReviewResponse } from '@models/performance/review-response.model';

@Component({
    selector: 'app-review-display',
    templateUrl: './review-display.template.html',
    styleUrls: ['./review-display.style.scss'],
})
export class ReviewDisplayComponent implements OnChanges, OnDestroy, OnInit {
    @Input() fields: any[] = [];
    @Input() review: any = {};
    @Input() response: ReviewResponse = new ReviewResponse();
    @Input() performanceReviewImprovements: boolean = false;

    @Output() saved: EventEmitter<ReviewResponse>;

    autoSaveTimerId: number | undefined;

    isActive = false;
    jsonResponse: any = {};
    autosave: Date;
    autosaveInterval: any = false;

    tab = 'fields';

    goals: any[] = [];
    feedbackReceived: any[] = [];
    feedbackGiven: any[] = [];
    people: any[] = [];
    peopleRating = 0;

    constructor(private element: ElementRef) {
        this.saved = new EventEmitter<any>();
    }

    ngOnInit(): void {
        this.renderElements();
    }

    ngOnDestroy(): void {
        this.autosaveClear();
        this.clearAutoSaveTimer();
    }

    isComplete(): boolean {
        if (this.performanceReviewImprovements) {
            return this.isCompleteWithOptionalValidation();
        }

        if (!Object.keys(this.jsonResponse) || !this.fields) {
            return false;
        }

        const lengthCheck = Object.keys(this.jsonResponse).length >= this.fields.length;

        for (const response in this.jsonResponse) {
            if (this.jsonResponse[response] === null || this.jsonResponse[response] === '') {
                return false;
            }
        }

        return lengthCheck;
    }

    submit(): void {
        this.response.jsonData = this.jsonResponse;
        this.response.submittedAt = new Date();
        this.response.autoSaved = false;
        this.saved.emit(this.response);
        this.exit(false);
    }

    onBack(): void {
        this.save(false);
        this.exit();
    }

    save(auto?: boolean): void {
        const hasResponseData = Object.keys(this.jsonResponse).length;
        const isValidAutosave = typeof auto !== 'undefined' && auto && hasResponseData;

        if (isValidAutosave) {
            this.response.jsonData = this.jsonResponse;
            this.response.autoSaved = true;
            this.saved.emit(this.response);
            this.autosave = new Date();
            return;
        }

        if (hasResponseData) {
            this.response.jsonData = this.jsonResponse;
            this.response.autoSaved = true;
            this.saved.emit(this.response);
        }
    }

    changeTab(tab: string): void {
        this.tab = tab;
        this.renderProgress();
        this.renderElements();
    }

    launch(): void {
        this.loadPerformanceData();

        if (!this.performanceReviewImprovements) {
            this.autosaveSetup();
        }

        const $container = $('.review-display-container');
        $container.width($(window).width()).height($(window).height());
        $container.fadeIn(() => {
            this.isActive = true;
            this.renderElements();
        });

        $(window).resize(() => {
            $container.width($(window).width()).height($(window).height());
        });
    }

    exit(save = true): void {
        this.autosaveClear();
        this.clearAutoSaveTimer();
        save && this.save(true);
        $('.review-display-container').fadeOut(() => {
            this.isActive = false;
            $('.ui.outlet').css({ 'overflow-y': 'scroll' });
        });
    }

    trackByFn(_index: number, item: any): any {
        return item.index;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['fields'] && changes['fields'].currentValue !== changes['fields'].previousValue) {
            this.renderElements();
        }
        if (changes['response'] && changes['response'].currentValue !== changes['response'].previousValue) {
            this.jsonResponse = this.response.jsonData || {};
        }
    }

    getGoalsBadgeType(): BadgeType {
        const numCompleteGoals = this.goals.filter((goal) => goal.isComplete).length;
        if (numCompleteGoals === 0) {
            return 'danger';
        } else if (numCompleteGoals === this.goals.length) {
            return 'success';
        }
        return 'warning';
    }

    getFeedbackBadgeType(feedbackLength: number): BadgeType {
        if (feedbackLength === 0) {
            return 'danger';
        }
        return 'success';
    }

    private renderProgress(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.progress').progress();
        });
    }

    triggerAutoSave(slow: boolean = false): void {
        if (!this.performanceReviewImprovements) {
            return;
        }

        this.clearAutoSaveTimer();
        let delay = 150; //ms
        if (slow) {
            //used when user is typing
            delay = 400;
        }
        this.autoSaveTimerId = window.setTimeout(() => {
            this.save(true);
        }, delay);
    }

    private clearAutoSaveTimer(): void {
        if (this.autoSaveTimerId) {
            window.clearTimeout(this.autoSaveTimerId);
        }
    }

    private renderMultiple(): void {
        setTimeout(() => {
            const _self = this;
            $(this.element.nativeElement)
                .find('.ui.radio.checkbox')
                .checkbox({
                    onChecked: function (): void {
                        const model = $(this).parent().data('model');
                        const value = $(this).parent().data('value');

                        _self['jsonResponse'][model] = value;
                        _self.triggerAutoSave();
                        _self.updateMultiple();
                    },
                });
            _self.updateMultiple();
        });
    }

    private updateMultiple(): void {
        setTimeout(() => {
            const _self = this;
            $(this.element.nativeElement)
                .find('.ui.radio.checkbox')
                .each(function (): void {
                    const model = $(this).data('model');
                    const value = _self['jsonResponse'][model];

                    if (value === $(this).data('value')) {
                        $(this).checkbox('set checked');
                    }
                });
        });
    }

    private isCompleteWithOptionalValidation(): boolean {
        for (const field in this.fields) {
            if (this.fields[field].isOptional === undefined || this.fields[field].isOptional === false) {
                const answerKey = 'question' + field;
                if (
                    this.jsonResponse[answerKey] === undefined ||
                    this.jsonResponse[answerKey] === null ||
                    this.jsonResponse[answerKey] === ''
                ) {
                    return false;
                }
            }
        }

        return true;
    }

    private renderRating(): void {
        setTimeout(() => {
            const _self = this;
            $(this.element.nativeElement)
                .find('.ui.rating')
                .rating({
                    onRate: function (value: any): void {
                        const model = $(this).data('model');
                        _self['jsonResponse'][model] = value;
                        _self.triggerAutoSave();
                    },
                });
        });
    }

    private renderBoolean(): void {
        setTimeout(() => {
            const _self = this;
            $('.true-false-scale div').each(function (_index: any, object: any): any {
                $(object).off('click');
                $(object).on('click', function (): void {
                    const value = $(this).data('value');
                    const model = $(this).parent('.true-false-scale').data('model');
                    _self['jsonResponse'][model] = value;
                    _self.triggerAutoSave();
                    _self.updateBoolean();
                });
            });
            _self.updateBoolean();
        });
    }

    private updateBoolean(): void {
        setTimeout(() => {
            const _self = this;
            $('.true-false-scale').each(function (): void {
                const model = $(this).data('model');
                const value = _self['jsonResponse'][model];
                const activeElement = $(this).find('[data-value="' + value + '"]');

                if (activeElement.length) {
                    $($(activeElement).parent('.true-false-scale').find('div')).removeClass('active');
                    $(activeElement).addClass('active');
                }
            });
        });
    }

    private renderScale(): void {
        setTimeout(() => {
            const _self = this;
            $('.agree-disagree-scale div').each(function (_index: any, object: any): void {
                $(object).off('click');
                $(object).on('click', function (): void {
                    const value = $(this).data('value');
                    const model = $(this).parent('.agree-disagree-scale').data('model');
                    _self['jsonResponse'][model] = value;
                    _self.triggerAutoSave();
                    _self.updateScale();
                });
            });
            _self.updateScale();
        });
    }

    private updateScale(): void {
        setTimeout(() => {
            const _self = this;
            $('.agree-disagree-scale').each(function (): void {
                const model = $(this).data('model');
                const value = _self['jsonResponse'][model];
                const activeElement = $(this).find('[data-value="' + value + '"]');
                if (activeElement.length) {
                    $($(activeElement).parent('.agree-disagree-scale').find('div')).removeClass('active');

                    $(activeElement).addClass('active');
                }
            });
        });
    }

    private renderElements(): void {
        this.renderRating();
        this.renderBoolean();
        this.renderScale();
        this.renderMultiple();
    }

    private autosaveSetup(): void {
        this.autosaveInterval = setInterval(() => {
            this.save(true);
        }, 10000);
    }

    private autosaveClear(): void {
        if (this.autosaveInterval) {
            clearInterval(this.autosaveInterval);
        }
    }

    private loadPerformanceData(): void {
        Goal.where('employeeId', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with('creator')
            .all()
            .then(([goals, _]) => {
                this.goals = goals;
            });
        Feedback.where('subjectId', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with(['creator', 'subject'])
            .all()
            .then(([feedbacks, _]) => {
                this.feedbackReceived = feedbacks;
            });
        Feedback.where('createdBy', this.review.subjectId)
            .where('periodStart', this.review.periodStart)
            .where('periodEnd', this.review.periodEnd)
            .with(['creator', 'subject'])
            .all()
            .then(([feedbacks, _]) => {
                this.feedbackGiven = feedbacks;
            });
    }
}
